<template>
  <div class="view-house-container">
    <PremiumPage
      :title="$t('members.view.title')"
      subtitle="Detailed profile and history for society member"
      showBack
    >
      <template #header-actions>
        <button
          v-if="member.status == 1"
          class="pt-add-btn-custom"
          @click="$router.push('/societyadmin/member/edit/' + member.id)"
        >
          <v-icon size="16" color="white">mdi-pencil-outline</v-icon>
          <span>Edit Profile</span>
        </button>
      </template>

      <div class="pa-6">
        <!-- ── Unified Detail Card ──────────────────────── -->
        <div class="unified-detail-card">
          <!-- Header section (Profile + Tabs) -->
          <div class="udc-header">
            <div class="udc-profile-top">
              <div class="udc-profile-left">
                <div class="udc-avatar-wrap">
                  <img
                    v-if="member.profile_url"
                    :src="API_BASE_URL + '/uploads/' + member.profile_url"
                    class="udc-img udc-img-clickable"
                    @click="openPhoto"
                    title="Click to view full photo"
                  />
                  <div
                    v-else
                    class="udc-placeholder"
                    :style="{
                      background: primaryColor
                        ? primaryColor + '15'
                        : '#e0e0e0',
                      color: primaryColor || '#333',
                    }"
                  >
                    {{
                      member.name ? member.name.charAt(0).toUpperCase() : "U"
                    }}
                  </div>
                </div>

                <div class="udc-info">
                  <div class="udc-title-row">
                    <h2 class="udc-name">{{ member.name }}</h2>
                    <span
                      class="udc-status-chip"
                      :class="{
                        'chip-active': member.status == 1,
                        'chip-pending': member.status == 0,
                        'chip-inactive': member.status == 2,
                      }"
                    >
                      <span class="chip-dot"></span>
                      {{
                        member.status == 1
                          ? "Active"
                          : member.status == 0
                          ? "Pending"
                          : "Inactive"
                      }}
                    </span>
                  </div>

                  <div class="udc-meta-row">
                    <div class="udc-meta-item">
                      <v-icon size="14" color="#64748B">
                        {{ member.unit_type == 2 ? 'mdi-store-outline' : 'mdi-home-outline' }}
                      </v-icon>
                      <span class="udc-meta-text font-weight-bold">
                        <span v-if="member.flat_name">{{ member.flat_name }}-</span>{{ member.house_number }}
                      </span>
                      <span
                        class="udc-member-type-badge"
                        :class="member.member_type == 2 ? 'type-tenant' : 'type-owner'"
                      >
                        {{ member.member_type == 2 ? 'Tenant' : 'Owner' }}
                      </span>
                    </div>
                    <span class="udc-meta-sep">|</span>
                    <div class="udc-meta-item">
                      <v-icon size="14" color="#64748B"
                        >mdi-phone-outline</v-icon
                      >
                      <span class="udc-meta-text font-weight-bold"
                        >{{ member.country_code }} {{ member.mobile_no }}</span
                      >
                    </div>
                    <span class="udc-meta-sep">|</span>
                    <div class="udc-meta-item">
                      <v-icon size="14" color="#64748B"
                        >mdi-email-outline</v-icon
                      >
                      <span class="udc-meta-text">{{
                        member.email || "No Email Provided"
                      }}</span>
                    </div>

                    <span class="udc-meta-sep">|</span>
                    <div class="udc-meta-item">
                      <v-icon size="14" color="#64748B"
                        >mdi-briefcase-outline</v-icon
                      >
                      <span class="udc-meta-text">{{
                        member.business || "Not Specified"
                      }}</span>
                    </div>
                  </div>

                  <div class="udc-actions mt-4" v-if="member.status == 0">
                    <button
                      class="udc-btn udc-btn-success"
                      @click="confirmAccept(member.id)"
                    >
                      <v-icon size="16" color="white" class="mr-1"
                        >mdi-check-circle-outline</v-icon
                      >
                      Approve Request
                    </button>
                    <button
                      class="udc-btn udc-btn-danger"
                      @click="confirmReject(member.id)"
                    >
                      <v-icon size="16" color="white" class="mr-1"
                        >mdi-close-circle-outline</v-icon
                      >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
              <div class="udc-profile-right">
                <button
                  class="udc-back-btn"
                  @click="$router.go(-1)"
                  :style="{ color: buttonColor, borderColor: buttonColor }"
                >
                  <v-icon
                    size="16"
                    class="arrow"
                    :style="{ color: buttonColor }"
                    >mdi-arrow-left</v-icon
                  >
                  BACK
                </button>
              </div>
            </div>

            <!-- Tab Navigation -->
            <div class="udc-tabs">
              <button
                v-for="(tab, idx) in tabs"
                :key="idx"
                class="udc-tab-btn"
                :class="{ active: activeTab === idx }"
                :style="
                  activeTab === idx
                    ? { color: primaryColor, borderBottomColor: primaryColor }
                    : {}
                "
                @click="switchTab(idx)"
              >
                <v-icon
                  size="18"
                  class="mr-2"
                  :color="activeTab === idx ? primaryColor : '#64748B'"
                >
                  {{ tab.icon }}
                </v-icon>
                {{ tab.text }}
              </button>
            </div>
          </div>

          <!-- Content Area -->
          <div class="udc-content-wrap">
            <div class="udc-content">
              <keep-alive>
                <component
                  :is="tabs[activeTab].component"
                  :member_id="$route.params.id"
                  :unit_mapping_id="unit_mapping_id"
                />
              </keep-alive>
            </div>
          </div>
        </div>
      </div>
    </PremiumPage>

    <!-- ── Modals ── -->
    <ConfirmModal
      v-model="AcceptDialog"
      type="primary"
      title="Approve Member"
      message="Are you sure you want to approve this member request? They will receive an automated notification."
      confirm-text="Yes, Approve"
      :loading="loading"
      @confirm="acceptMemeberRequest(accept_id)"
    />

    <ConfirmModal
      v-model="RejectDialog"
      type="danger"
      title="Reject Request"
      message="Are you sure you want to reject this member request?"
      confirm-text="Yes, Reject"
      :loading="loading"
      @confirm="rejectMemberRequest(reject_id)"
    />

    <!-- ── Photo Viewer ──────────────────────── -->
    <MediaViewer
      v-model="photoViewer.open"
      :items="photoViewer.items"
      :index="0"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import MemberBills from "./MemberBills.vue";
import MemberVisitors from "./MemberVisitors.vue";
import MemberGatePasses from "./MemberGatePasses.vue";
import MemberTickets from "./MemberTickets.vue";
import MemberDelegates from "./MemberDelegates.vue";
import MemberVehicles from "./MemberVehicles.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  name: "ViewMember",

  components: {
    MemberBills,
    MemberVisitors,
    MemberGatePasses,
    MemberTickets,
    MemberDelegates,
    MemberVehicles,
    ConfirmModal,
    MediaViewer,
  },

  data() {
    return {
      user: {},
      loading: false,
      member: { name: "", email: "", mobile_no: "", status: 1 },
      activeTab: 0,
      unit_mapping_id: null,
      AcceptDialog: false,
      RejectDialog: false,
      accept_id: null,
      reject_id: null,
      photoViewer: { open: false, items: [] },
      tabs: [
        {
          text: "Bills",
          icon: "mdi-credit-card-outline",
          component: "MemberBills",
        },
        {
          text: "Visitors",
          icon: "mdi-account-clock-outline",
          component: "MemberVisitors",
        },
        {
          text: "Gate Passes",
          icon: "mdi-shield-check-outline",
          component: "MemberGatePasses",
        },
        {
          text: "Tickets",
          icon: "mdi-ticket-outline",
          component: "MemberTickets",
        },
        {
          text: "Delegates",
          icon: "mdi-account-multiple-outline",
          component: "MemberDelegates",
        },
        {
          text: "Vehicles",
          icon: "mdi-car-outline",
          component: "MemberVehicles",
        },
      ],
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "isDark"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    getMemberDetail() {
      this.$axios
        .get(`societyadmin/members/${this.$route.params.id}/details`)
        .then((response) => {
          if (response.data.status) {
            this.member = response.data.data.member;
          } else {
            this.showError(response.data.message);
          }
        });
    },

    confirmAccept(id) {
      this.accept_id = id;
      this.AcceptDialog = true;
    },
    confirmReject(id) {
      this.reject_id = id;
      this.RejectDialog = true;
    },

    acceptMemeberRequest(id) {
      this.loading = true;
      this.$axios
        .post("societyadmin/members/request/accept/" + id)
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.AcceptDialog = false;
            this.setAlert({
              showAlert: true,
              variant: "green",
              timeout: "2000",
              message: response.data.message,
            });
            this.$router.push({ name: "societyadminHouseRequest" });
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    rejectMemberRequest(id) {
      this.loading = true;
      this.$axios
        .post("societyadmin/members/request/reject/" + id)
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.RejectDialog = false;
            this.setAlert({
              showAlert: true,
              variant: "green",
              timeout: "2000",
              message: response.data.message,
            });
            this.$router.push({ name: "societyadminHouseRequest" });
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red",
        timeout: "3000",
        message: msg,
      });
    },

    openPhoto() {
      if (!this.member.profile_url) return;
      this.photoViewer.items = [{
        url: this.API_BASE_URL + '/uploads/' + this.member.profile_url,
        type: "image",
        name: this.member.name || "Profile Photo",
      }];
      this.photoViewer.open = true;
    },

    switchTab(idx) {
      this.activeTab = idx;
      const tabName = this.tabs[idx].text.toLowerCase().replace(/\s+/g, "_");
      if (this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName },
        }).catch(() => {});
      }
    },

    syncTabFromQuery() {
      const tabQuery = this.$route.query.tab;
      if (tabQuery) {
        const foundIdx = this.tabs.findIndex(
          (t) => t.text.toLowerCase().replace(/\s+/g, "_") === tabQuery
        );
        if (foundIdx !== -1) {
          this.activeTab = foundIdx;
        }
      }
    },
  },

  watch: {
    "$route.query.tab": {
      handler() {
        this.syncTabFromQuery();
      },
    },
  },

  mounted() {
    this.user = JSON.parse(localStorage.getItem("user") || "{}");
    this.syncTabFromQuery();
    this.getMemberDetail();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.sec-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.pt-add-btn-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 42px;
  border: none;
  border-radius: 12px;
  background: var(--brand, #0d9972);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

/* ── Unified Detail Card ── */
.unified-detail-card {
  /* background: var(--surface); */
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
}

/* Header section */
.udc-header {
  padding: 16px 16px 0px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.udc-profile-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  position: relative;
}

.udc-profile-left {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-right: 100px;
}

.udc-avatar-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  background: var(--surface2);
}
.udc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.udc-img-clickable {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.udc-img-clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(0,0,0,0.18);
}
.udc-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 800;
}

.udc-info {
  flex: 1;
}

.udc-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.udc-name {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--tx);
  margin: 0;
  letter-spacing: -0.01em;
}

/* Status chip */
.udc-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.chip-active {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}
.chip-active .chip-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
}
.chip-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}
.chip-pending .chip-dot {
  background: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.25);
}
.chip-inactive {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}
.chip-inactive .chip-dot {
  background: #94a3b8;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.2);
}

.udc-status-badge {
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.badge-active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.badge-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.udc-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}
.udc-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.udc-meta-text {
  font-size: 13.5px;
  color: var(--tx2);
  font-weight: 500;
}
.udc-meta-sep {
  color: var(--border);
  font-size: 14px;
  opacity: 0.5;
}

/* Owner / Tenant badge in header */
.udc-member-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-left: 4px;
}
.type-owner {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.25);
}
.type-tenant {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.25);
}

.udc-profile-right {
  position: absolute;
  top: 0;
  right: 0;
  flex-shrink: 0;
}

/* Back Button */
/* Back Button */
.udc-back-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 0 14px 0 10px !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  letter-spacing: 0.05em !important;
  border-radius: 8px !important;
  color: var(--btn-color) !important;
  border: 1px solid var(--btn-color) !important;
  background: var(--surface2) !important;
  height: 38px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
}

.udc-back-btn:hover {
  background: var(--surface2) !important;
  border-color: var(--btn-color) !important;
}

.arrow {
  color: var(--btn-color) !important;
}

/* Actions */
.udc-actions {
  display: flex;
  gap: 12px;
}
.udc-btn {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
.udc-btn:hover {
  transform: translateY(-1px);
}
.udc-btn-success {
  background: #10b981;
  color: #fff;
}
.udc-btn-danger {
  background: #ef4444;
  color: #fff;
}

/* Tabs */
.udc-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}
.udc-tabs::-webkit-scrollbar {
  display: none;
}
.udc-tab-btn {
  background: none;
  border: none;
  padding: 16px;
  font-size: 14px;
  font-weight: 600;
  color: var(--tx3);
  border-bottom: 3.5px solid transparent;
  white-space: nowrap;
  transition: all 0.2s ease;
  cursor: pointer;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.udc-tab-btn:hover {
  color: var(--tx);
  background: rgba(0, 0, 0, 0.02);
}
.udc-tab-btn.active {
  font-weight: 700;
}

/* Content Area */
.udc-content-wrap {
  /* background: var(--surface2); */
  min-height: 500px;
  padding: 16px;
}
.udc-content {
  /* Remove any extra padding if components have their own */
}

/* Responsive */
@media (max-width: 768px) {
  .udc-header {
    padding: 20px 20px 0 20px;
  }
  .udc-profile-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .udc-profile-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .udc-meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .udc-meta-sep {
    display: none;
  }
  .udc-content-wrap {
    padding: 16px;
  }
}
</style>
